import React, { useState, useContext, useEffect } from 'react';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { useAddToCart } from '@/context/addToCartProvider';
import { ProductContext } from '@/customercenter-ui/components/product/details/contexts';
import { useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { ORDER_TYPES } from '../../../../constants/constant';
import Minus from '../icons/minus';
import Add from '../icons/plus';
import { Button } from '../ui/button';

interface counterProps {
  initialValue?: number;
  onChange?: any;
  backOrderValue?: number;
  isPdp?: boolean;
  lineItemId?: string;
  toggle?: () => void;
  isEditCart?: boolean;
  setMaxQuantityWarningMessage?: (message: string) => void;
  sizeGroupLength?: number;
  widthGroupLength?: number;
  selectedSize?: string;
  selectedWidth?: string;
  isAvaialbleMsgReq?: any;
  onCartHeaderChange?: any;
  availabilityMsg?: string;
  stockValue?: number;
}

const Counter: React.FC<counterProps> = ({
  initialValue,
  onChange,
  backOrderValue,
  isEditCart,
  setMaxQuantityWarningMessage,
  sizeGroupLength,
  widthGroupLength,
  selectedSize,
  selectedWidth,
  isAvaialbleMsgReq,
  onCartHeaderChange,
  stockValue,
}) => {
  const [count, setCount] = useState<any>(initialValue ? initialValue : 1); // Default value set to 0
  const [inputValue, setInputValue] = useState('');
  const { availabilityMessage, setError, setSizeErrorMessage, setWidthErrorMessage } = useAddToCart();
  const { initialSize } = useContext(ProductContext);
  const projectSettings: ProjectSettings = useProjSettings();
  const [disableButton, setDisableButton] = useState(false);
  const { data: cartData } = useCart(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isStockValueLoading, setIsStockValueLoading] = useState(true);
  const availableQty =
    initialSize?.backOrderValue && initialSize?.backOrderValue > 0
      ? initialSize?.availability?.noChannel?.availableQuantity + initialSize?.backOrderValue
      : initialSize?.availability?.noChannel?.availableQuantity;

  const handleIncrement = () => {
    if ((availableQty && count < availableQty) || cartData?.orderType == ORDER_TYPES.MAIL_ORDER) {
      setCount((prevCount: number) => prevCount + 1);
      onChange(count + 1);
    }
  };

  const handleDecrement = async () => {
    setCount((prevCount: number) => prevCount - 1);
    onChange(count - 1);
    onCartHeaderChange?.(count - 1);
    if (count <= availableQty) {
      setDisableButton(false);
      setMaxQuantityWarningMessage?.('');
    }
  };

  const stockQuantity = stockValue;
  useEffect(() => {
    if (stockValue || backOrderValue) {
      setIsStockValueLoading(false);
    }
  }, [stockValue, backOrderValue]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const availableQuantity =
    stockQuantity !== undefined && backOrderValue && backOrderValue > 0
      ? stockQuantity + backOrderValue
      : stockQuantity ?? 0;

  const handleChange = async (e: any) => {
    if ((sizeGroupLength && !selectedSize) || (widthGroupLength && !selectedWidth)) {
      setError(true);
      if (!selectedSize) {
        setSizeErrorMessage(mapLocaleToMeaningfulFormat().requiredSize);
      }
      if (!selectedWidth) {
        setWidthErrorMessage(mapLocaleToMeaningfulFormat().requiredWidth);
      }
    } else {
      setError(false);
      const maxAvailabilityValue = availableQty === 0 ? 1 : availableQty;
      const targetValue = parseInt(e.target.value);
      if (e.target.value === '') {
        setCount('');
      } else {
        setCount(targetValue < availableQty ? targetValue : maxAvailabilityValue);
        onChange(targetValue < availableQty ? targetValue : maxAvailabilityValue);
        onCartHeaderChange?.(targetValue < availableQty ? targetValue : maxAvailabilityValue);
      }
      if (e.target.value === 0) {
        if (isEditCart) {
          setCount(0);
          onChange(0);
          onCartHeaderChange?.(0);
        } else {
          setCount(1);
          onChange(1);
          onCartHeaderChange?.(1);
        }
      } else {
        onChange(count);
        onCartHeaderChange?.(count);
      }
      if (availableQty && targetValue >= availableQty) {
        setDisableButton(true);
        setMaxQuantityWarningMessage?.(`${mapLocaleToMeaningfulFormat([availableQty?.toString()]).onlyCanBeOrdered}`);
      } else {
        setDisableButton(false);
        setMaxQuantityWarningMessage?.('');
      }
    }
  };

  const handleBlur = async () => {
    if (count === '' || count === 0) {
      if (isEditCart) {
        setCount(0);
        onChange(0);
        onCartHeaderChange?.(0);
      } else {
        setCount(1);
        onChange(1);
        onCartHeaderChange?.(1);
      }
    } else {
      onChange(count);
      onCartHeaderChange?.(count);
    }
  };

  useEffect(() => {
    if (availableQty) {
      const maxAvailabilityValue = availableQty === 0 ? 1 : availableQty;
      const newCount = count < availableQty ? count : maxAvailabilityValue;
      if (count !== newCount) {
        setCount(isAvaialbleMsgReq ? newCount : count);
        onChange(newCount);
        onCartHeaderChange?.(newCount);
      }
    }
    if (projectSettings?.maxItemQuantity && count > projectSettings?.maxItemQuantity) {
      setCount(projectSettings?.maxItemQuantity);
    }
  }, [initialSize, count, onChange, onCartHeaderChange]);

  useEffect(() => {
    if (count >= availableQty) {
      setMaxQuantityWarningMessage?.(`${mapLocaleToMeaningfulFormat([count.toString()]).onlyCanBeOrdered}`);
    } else {
      setMaxQuantityWarningMessage?.('');
    }
  }, [initialSize, count, availableQty]);

  const handleInputKeyPress = (e: any) => {
    if ((sizeGroupLength && !selectedSize) || (widthGroupLength && !selectedWidth)) {
      setError(true);
      if (!selectedSize) {
        setSizeErrorMessage(mapLocaleToMeaningfulFormat().requiredSize);
      }
      if (!selectedWidth) {
        setWidthErrorMessage(mapLocaleToMeaningfulFormat().requiredWidth);
      }
    } else {
      setError(false);

      if (e?.key === 'Enter' || e?.key === '-' || e?.key === '+') {
        e?.preventDefault();
        const value = parseInt(inputValue);
        if (!isNaN(value) && value >= 0) {
          setCount(value);
        } else {
          setInputValue(count?.toString());
        }
      }
    }
  };
  return (
    <div className="flex items-center gap-3.5">
      {((!isEditCart && count < 1) || availabilityMessage === mapLocaleToMeaningfulFormat().outOfStock) &&
      cartData?.orderType != ORDER_TYPES.MAIL_ORDER ? (
        <></>
      ) : (
        <Button
          variant="outline"
          size="icon"
          onClick={handleDecrement}
          disabled={!isEditCart && count < 2}
          aria-label="Decrease quantity"
          className={`flex h-8 w-8 items-center justify-center rounded-full bg-silverGray ${
            (isEditCart && count === 0) || (!isEditCart && count === 1 && cartData?.orderType != ORDER_TYPES.MAIL_ORDER)
              ? 'cursor-not-allowed'
              : ''
          }`}
        >
          <Minus />
        </Button>
      )}
      <label htmlFor="itemCount" className="sr-only">
        Item Count
      </label>
      <input
        autoCorrect="off"
        autoComplete="Number"
        inputMode="numeric"
        id="itemCount"
        type="number"
        value={count < 0 ? 0 : count}
        onChange={handleChange}
        onKeyPress={handleInputKeyPress}
        className="numberInputCounter gbunmask flex h-11 w-11 appearance-none items-center justify-center rounded-sm border border-silverGray text-center"
        max={projectSettings?.maxItemQuantity}
        min={isEditCart ? 0 : 1}
        onBlur={handleBlur}
        aria-label="Item Count"
        disabled={
          availabilityMessage === mapLocaleToMeaningfulFormat().outOfStock &&
          cartData?.orderType != ORDER_TYPES.MAIL_ORDER
        }
      />
      {availabilityMessage === mapLocaleToMeaningfulFormat().outOfStock &&
      cartData?.orderType != ORDER_TYPES.MAIL_ORDER ? (
        <></>
      ) : (
        <Button
          variant="outline"
          size="icon"
          onClick={handleIncrement}
          disabled={
            (count >= (projectSettings?.maxItemQuantity ?? 99) || disableButton) &&
            cartData?.orderType != ORDER_TYPES.MAIL_ORDER
          }
          aria-label="Increase quantity"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-silverGray"
        >
          <Add />
        </Button>
      )}
    </div>
  );
};

export default Counter;
